import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";
import {
  clearCookie,
  getCookie,
  getUserDetails,
  initialUserDetails,
  isSameUtcDay,
  refreshTokens,
  type UserDetails,
} from "../utils/account";

const USER_KEY = "teuteuf-user";

interface UserContextProps {
  isLoaded: boolean;
  userDetails: UserDetails;
  // Single source of truth for "has Thursday Thirty premium" — used by the
  // ad units so they can never disagree about whether to run an auction.
  isPremium: boolean;
  signOut: () => void;
}

const UserContext = createContext<UserContextProps>({
  isLoaded: false,
  userDetails: initialUserDetails,
  isPremium: false,
  signOut: () => {},
});

// eslint-disable-next-line react-refresh/only-export-components
export const useUser = () => useContext(UserContext);

export const UserProvider = ({ children }: { children: ReactNode }) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [userDetails, setUserDetails] =
    useState<UserDetails>(initialUserDetails);

  const signOut = () => {
    clearCookie("isLoggedIn");
    clearCookie("jwt");
    localStorage.removeItem(USER_KEY);
    setUserDetails(initialUserDetails);
  };

  useEffect(() => {
    const loadUser = async () => {
      const isLoggedIn = getCookie("isLoggedIn") === "true";

      if (!isLoggedIn) {
        setIsLoaded(true);
        return;
      }

      // Show cached user immediately; refresh from the server below.
      const cachedUser = localStorage.getItem(USER_KEY);
      if (cachedUser) {
        try {
          setUserDetails(JSON.parse(cachedUser));
        } catch {
          localStorage.removeItem(USER_KEY);
        }
      }

      try {
        const encodedRefreshTime = getCookie("refreshDate");
        const refreshTime = encodedRefreshTime
          ? decodeURIComponent(encodedRefreshTime)
          : null;
        if (!refreshTime || !isSameUtcDay(refreshTime)) {
          await refreshTokens();
        }

        const response = await getUserDetails();
        if (response) {
          const newUserDetails: UserDetails = {
            loggedIn: true,
            firstName: response.firstName,
            photoURL: response.photoURL,
            premiumGames: response.premiumGames ?? [],
          };
          setUserDetails(newUserDetails);
          localStorage.setItem(USER_KEY, JSON.stringify(newUserDetails));
        } else {
          signOut();
        }
      } catch (e) {
        console.error("Failed to load user:", e);
        signOut();
      } finally {
        setIsLoaded(true);
      }
    };

    loadUser();
  }, []);

  const isPremium =
    userDetails.loggedIn &&
    userDetails.premiumGames.includes("thursday-thirty");

  return (
    <UserContext.Provider value={{ isLoaded, userDetails, isPremium, signOut }}>
      {children}
    </UserContext.Provider>
  );
};
